import "./reportAnon.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { Layer } from "@inovitas/infra3dsdk/dist/types/mm";
import { GeoFrame } from "@inovitas/infra3dsdk/dist/types/mm/geoframe/geoframe";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ClientConfig from "shared/config";
import AnonymImage from "shared/assets/anonym.png";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import PopoverElement from "shared/common/utils/popover/PopoverElement";

interface props {
  anonProject: string;
}

export default function ReportAnon({ anonProject }: props) {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const viewer = defaultStore((state) => state.viewer);
  const reportAnonymization = defaultStore(
    (state) => state.reportAnonymization
  );
  const changeReportAnonymization = defaultStore(
    (state) => state.changeReportAnonymization
  );
  const riaLayer = defaultStore((state) => state.riaLayer);
  const changeRiaLayer = defaultStore((state) => state.changeRiaLayer);

  const [frameFeatureTool, setFrameFeatureTool] =
    useState<mm.FrameFeatureTool>();
  const anonLayerRef = useRef<mm.Layer | undefined | null>(null);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);

  const { t } = useTranslation();

  //close report anon window
  const handleReportAnonymization = () => {
    changeReportAnonymization(!reportAnonymization);
  };

  //add new layer
  const addNewLayer = () => {
    if (viewer === undefined) return;
    const layerhandler = viewer.getScene().getLayerHandler();
    const api = new mm.Infra3DGeoserver(
      ClientConfig.geofeatureserverURL,
      anonProject,
      viewer
    );
    api.readlayerdefinition(
      function (ldefs): any {
        const ldefs_obj: any = {};
        ldefs["layers"].map(function (ldef: any) {
          ldefs_obj[ldef["layername"]] = ldef;
        });
        const style = {
          rules: [
            {
              filter: "",
              params: {
                fill: [0.9568, 0.5137, 0.3372, 0.3],
                stroke: [0.9568, 0.5137, 0.3372, 0.3],
              },
              symbolizer: "polygon",
            },
          ],
          stylename: "status",
        };
        viewer.getScene().userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE.LAYERADDED,
          (layer: Layer) => {
            if (layer.layername !== anonProject + ".box") return;
            anonLayerRef.current = layer;
            if (frameFeatureTool) {
              frameFeatureTool.framefeaturecollection.setDefaultProps(
                layer.getDefaultProperties()
              );
            }
            changeRiaLayer(layer);
          },
          undefined
        );
        layerhandler.addLayer(
          mm.LAYERTYPE.FRAMEFEATURELAYER,
          mm.Infra3DLayer.optionsFromLayerdefinition(ldefs_obj["box"], {
            api: api,
            style: style,
            hideinlayertree: true,
          })
        );
      },
      function (error): any {
        setError(true);
        console.log(error);
      }
    );
  };

  // add new feature
  const onLabelCreated = (obj: any) => {
    if (anonLayerRef.current) {
      const geojson = JSON.parse(obj.ffc.toGeoJsonString());
      const geoframe: GeoFrame | undefined = viewer
        ?.getScene()
        .getCurrentGeoframe();
      for (let i = 0; i < geojson.features.length; i++) {
        if (geoframe)
          geojson.features[i].geometry = {
            type: "Point",
            coordinates: geoframe.getPosition(),
          };
        geojson.features[i].properties.status = "suggested";
        geojson.features[i].properties.objecid = null;
      }

      anonLayerRef.current.setEditMode(true);
      anonLayerRef.current.getLayerEditor().insertFeatures(geojson);

      handleSelection();
    }
  };

  //add frame tool if viewer is defined
  useEffect(() => {
    if (viewer !== undefined) {
      setFrameFeatureTool(
        viewer.getScene().getToolHandler().createFrameFeatureTool(
          {
            finishedcbf: onLabelCreated,
          },
          "ria"
        )
      );
    }
  }, [viewer]);

  //steps array
  const steps = [
    {
      label: t("ria.step_one_title"),
      description: t("ria.step_one_description"),
    },
    {
      label: t("ria.step_two_title"),
      description: t("ria.step_two_description"),
    },
    {
      label: t("ria.step_three_title"),
      description: t("ria.step_three_description"),
    },
  ];

  useEffect(() => {
    //add new layer if report anon is true or make active step 0 if not
    if (reportAnonymization) {
      addNewLayer();
    } else {
      if (activeStep === 1) {
        handleBack();
      }
    }
    //if report anon is false and rialeyr is not undefined than remove layer
    if (!reportAnonymization && riaLayer !== undefined) {
      viewer?.getScene().getLayerHandler().removeLayer(riaLayer);
      changeRiaLayer(undefined);
    }
  }, [reportAnonymization]);

  //enable or disable frame tool
  useEffect(() => {
    if (frameFeatureTool !== undefined) {
      if (
        activeStep === 0 &&
        reportAnonymization &&
        anonLayerRef.current !== null
      ) {
        frameFeatureTool.enable();
      } else if (
        activeStep !== 0 &&
        reportAnonymization &&
        anonLayerRef.current !== null
      ) {
        frameFeatureTool.disable();
      } else {
        frameFeatureTool.disable();
        setActiveStep(0);
      }
    }
  }, [activeStep, frameFeatureTool, reportAnonymization, anonLayerRef.current]);

  //go one step forward
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
    }
  };

  //go one step back
  const handleBack = () => {
    setActiveStep((prevActiveStep: number) => prevActiveStep - 1);
  };

  //close report anon window
  const handleCloseEvents = () => {
    setActiveStep(0);
    handleReportAnonymization();
  };

  //reset active step to add another anon layer
  const handleSelectAnother = () => {
    setActiveStep(0);
  };

  //change active step to 1 after selection
  const handleSelection = () => {
    setActiveStep(1);
  };

  //discard changes
  const no = () => {
    if (anonLayerRef.current) {
      anonLayerRef.current.reset();
      anonLayerRef.current.reload();
      anonLayerRef.current.setEditMode(false);
      handleBack();
    }
  };

  //save changes
  const yes = () => {
    if (anonLayerRef.current) {
      anonLayerRef.current.save(() => {
        if (anonLayerRef.current) anonLayerRef.current.setEditMode(false);
        return {};
      });
      handleNext();
    }
  };

  return (
    <PopoverElement
      visible={reportAnonymization}
      handleClose={handleReportAnonymization}
      title={t("ria.title")}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={1}
        height={"100%"}
        sx={{ color: darkTheme ? "white" : "darkblue.main" }}
      >
        <img width={"100%"} src={AnonymImage} />
        <Typography variant="body1">{t("ria.message")}</Typography>
        {anonLayerRef.current !== null && (
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  classes={{
                    label: darkTheme
                      ? "infra3d-stepLabelDark"
                      : "infra3d-stepLabelLight",
                  }}
                >
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2">{step.description}</Typography>
                  <Box sx={{ mb: 2 }}>
                    {activeStep === 1 && (
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ mt: 1, mr: 1 }}
                        onClick={() => monitor(apm, "clicked-yes | RIA", yes)}
                      >
                        {t("ria.stepper_yes")}
                      </Button>
                    )}
                    {activeStep === 1 && (
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ mt: 1, mr: 1 }}
                        onClick={() => monitor(apm, "clicked-no | RIA", no)}
                      >
                        {t("ria.stepper_no")}
                      </Button>
                    )}
                    {activeStep === 2 && (
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ mt: 1, mr: 1 }}
                        onClick={() =>
                          monitor(
                            apm,
                            "select-another-object | RIA",
                            handleSelectAnother
                          )
                        }
                      >
                        {t("ria.stepper_select_another_object")}
                      </Button>
                    )}
                    {activeStep === 2 && (
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ mt: 1, mr: 1 }}
                        onClick={() =>
                          monitor(apm, "clicked-close | RIA", handleCloseEvents)
                        }
                      >
                        {t("ria.stepper_close")}
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
        {anonLayerRef.current === null && !error && (
          <Skeleton variant="rounded" height={100}></Skeleton>
        )}
        {error && (
          <Alert severity="error" variant={darkTheme ? "filled" : "standard"}>
            <Typography variant="body1">{t("ria.error")}</Typography>
          </Alert>
        )}
      </Box>
    </PopoverElement>
  );
}
